/**
 * Stage E — Simplified Stop: "I'm done" as one truthful act.
 *
 * The server (`finish_my_work`) decides everything that matters under the
 * ticket lock; this module only shapes what the technician is asked:
 *
 *   ONLY_MINE           another technician is still running OR paused on the
 *                       ticket → only this technician's work ends. Nothing
 *                       about the vehicle or the ticket's ownership changes,
 *                       so nothing about them is asked.
 *   FINAL_ASK_LOCATION  this is the last live BOH work and the vehicle is not
 *                       somewhere a completed car may stay (a bay, off site,
 *                       unknown) → "Where are you leaving the vehicle?"
 *   FINAL_STAYS         last live BOH work, vehicle already parked on site →
 *                       no location question; it stays where it is (the
 *                       technician may say it is somewhere else).
 *
 * In both FINAL modes the Stop IS the technician's declaration that the BOH
 * work is complete (20270102100000): the ticket is completed, the vehicle is
 * left on site, and the ticket goes to the front office — no extra question.
 * Operational completion is not pickup, payment or closeout.
 *
 * oct20 (FD-02 / FD-03 / D-2) — the final Stop completes the ticket only when
 * the work is done. If the technician leaves a job "not finished" (an
 * inspection answered "No" included) or something they cannot clear blocks
 * completion, the Stop still ends their work and hands the ticket to the front
 * office NOT completed. A rollover day with a live next day ends that day's
 * work, never the repair. A diagnostic in their own work still needs its
 * outcome before the diagnosis can end (CORE-AJ :46).
 *
 * OCT28 #14 — on a day of a multi-day repair whose next day is still booked, the
 * last technician chooses (nothing preselected): "Today's Work Complete" (the
 * existing Stop; the repair continues) or "Entire Repair Complete" (the server's
 * finish_entire_repair: completes the repair now and releases its unused later
 * days, all or nothing). OCT28 #14-c — an inspection is answered, never completed
 * by the Stop on its own; left incomplete or not performed it never blocks.
 *
 * Pure: no React, no Supabase. Every surface uses this same model.
 */
import type { OffSiteReason, VehicleLocationTarget } from '@/lib/location/model';

/** Where a finishing technician may leave the vehicle — never a bay. */
export type FinishDestination = Exclude<VehicleLocationTarget, { kind: 'BAY' }>;

export interface FinishWorkContext {
  myState: 'ACTIVE' | 'PAUSED' | 'NONE';
  /** Another technician is running or has an unresolved pause (server, under no lock — advisory). */
  othersLive: boolean;
  finalWorker: boolean;
  /** This ticket holds the vehicle's location (a dependent ticket does not). */
  holdsLocation: boolean;
  location: {
    kind: string; bayId: string | null; parkingSpotId: string | null; offSiteReason: string | null;
  } | null;
  needsDestination: boolean;
  /**
   * Final worker only: open jobs no episode ever had in scope. Each needs the
   * technician's disposition before the Stop can complete the ticket.
   */
  unscopedJobs: ReadonlyArray<{ jobId: string; name: string }>;
  /**
   * oct20 D-2: this ticket is a day of a multi-day repair with a live next
   * day. Its Stop ends TODAY's work — never the repair (20270114100000).
   */
  rolloverSegmentContinues?: boolean;
  /** Core-100 direction 9: a Detail Specialist's last Stop on a mixed visit (not the final worker). */
  completionWithheld?: boolean;
  /** oct20 FD-02 (final, repair ending): every open job of the repair the technician may leave unfinished. */
  openJobs?: ReadonlyArray<{ jobId: string; name: string; kind: 'diagnostic' | 'inspection' | null; inMyScope: boolean }>;
  /** oct20 CORE-AJ :46: diagnostic jobs in MY work whose outcome this Stop must record. */
  diagnosticOutcomeRequired?: ReadonlyArray<{ jobId: string; name: string }>;
  /** oct20 FD-02: what this Stop cannot clear — the ticket will be handed to FOH, not completed. */
  completionBlockers?: readonly string[];
  /**
   * oct20a S1: this Stop ends MY diagnostic session but not the diagnosis (a colleague still works it, or
   * the repair continues on its next day): each of these needs what my session found — a session
   * conclusion, never the diagnosis result — or its outcome if the diagnosis really is done.
   */
  diagnosticSessionConclusionRequired?: ReadonlyArray<{ jobId: string; name: string }>;
}

/**
 * What the final technician says about a job.
 *   COMPLETED / NOT_PERFORMED — for a job no measured work points at.
 *   NOT_FINISHED (oct20 FD-02) — any open job: "not done; leave it open". The
 *   ticket is then NOT completed: the work stops and goes to the front office.
 */
export type JobDisposition = 'COMPLETED' | 'NOT_PERFORMED' | 'NOT_FINISHED';

export const JOB_DISPOSITION_LABELS: Record<JobDisposition, string> = {
  COMPLETED: 'Completed',
  NOT_PERFORMED: 'Not performed',
  NOT_FINISHED: 'Not finished',
};

/** The labels for an inspection: "not finished" is how FOH reads INCOMPLETE. */
export const INSPECTION_DISPOSITION_LABELS: Record<JobDisposition, string> = {
  COMPLETED: 'Completed',
  NOT_PERFORMED: 'Not performed',
  NOT_FINISHED: 'Incomplete',
};

/**
 * OCT28 #14-c — the answers offered for a job the Stop asks about. An inspection with recorded work was performed, so
 * "Not performed" is not offered for it (the server refuses it too); no findings is not proof it was not performed.
 */
export function dispositionOptions(job: { kind?: 'inspection' | null; worked?: boolean }): Array<{ value: JobDisposition; label: string }> {
  const labels = job.kind === 'inspection' ? INSPECTION_DISPOSITION_LABELS : JOB_DISPOSITION_LABELS;
  return (Object.keys(labels) as JobDisposition[])
    .filter((d) => !(job.kind === 'inspection' && job.worked === true && d === 'NOT_PERFORMED'))
    .map((d) => ({ value: d, label: labels[d] }));
}

/** OCT28 #14 — what the last technician on a day of a multi-day repair says they finished. */
export type StopScope = 'TODAY' | 'ENTIRE_REPAIR';

export const STOP_SCOPE_CHOICES: ReadonlyArray<{ value: StopScope; label: string; detail: string }> = [
  { value: 'TODAY', label: "Today's Work Complete",
    detail: 'Stop for today. The repair continues on its next scheduled day.' },
  { value: 'ENTIRE_REPAIR', label: 'Entire Repair Complete',
    detail: 'All of the repair work is finished early. The repair is completed, its later scheduled days are freed up, and it goes to the front office.' },
];

/**
 * OCT28 #14 — the two-choice question is asked only of the LAST technician on a day whose next day is still booked
 * (a colleague still working → only my work ends; a one-day repair → the ordinary Stop).
 */
export function offersEntireRepairChoice(ctx: FinishWorkContext | null): boolean {
  return ctx?.finalWorker === true && ctx.rolloverSegmentContinues === true;
}

/** The wire shape finish_my_work validates — one entry per decided job. */
export function dispositionPayload(
  d: Readonly<Record<string, JobDisposition>>,
): Array<{ job_id: string; disposition: JobDisposition }> {
  return Object.entries(d).map(([job_id, disposition]) => ({ job_id, disposition }));
}

/** Every job that needs a disposition has one. */
export function allDispositioned(
  jobs: ReadonlyArray<{ jobId: string }>, d: Readonly<Record<string, JobDisposition>>,
): boolean {
  return jobs.every((j) => d[j.jobId] === 'COMPLETED' || d[j.jobId] === 'NOT_PERFORMED' || d[j.jobId] === 'NOT_FINISHED');
}

/**
 * oct20 D-2 / FD-02: does THIS Stop end the repair's BOH work? Only the final
 * worker's, and never on a rollover day with a live next day. A Detail
 * Specialist's withheld Stop is not final (the server says so).
 */
export function stopEndsRepair(ctx: FinishWorkContext | null, scope: StopScope | null = null): boolean {
  return ctx?.finalWorker === true && (ctx.rolloverSegmentContinues !== true || scope === 'ENTIRE_REPAIR');
}

/** A diagnostic/inspection job the Stop dialog asks about (my_diagnostic_capture_candidates). */
export interface StopCaptureCandidate { jobId: string; jobName: string; lockType: 'diagnostic' | 'inspection' }

export interface StopConfirmInput {
  ctx: FinishWorkContext | null;
  ctxLoaded: boolean;
  locationOk: boolean;
  unresolvedJobs: ReadonlyArray<{ jobId: string }>;
  dispositions: Readonly<Record<string, JobDisposition>>;
  candidates: ReadonlyArray<StopCaptureCandidate>;
  /** Jobs whose findings are recorded (confirmed or fully answered). */
  capturedJobIds: ReadonlySet<string>;
  /** Jobs answered "No — not completed right now". */
  skippedJobIds: ReadonlySet<string>;
  /** The technician's own work state: a paused technician has no open interval to record findings on. */
  endingPausedWork: boolean;
  /** oct20a S1: diagnostic jobs whose session conclusion this Stop needs (sessionConclusionJobs). */
  sessionConclusionJobs?: ReadonlyArray<{ jobId: string; name: string }>;
  /** oct20a S1: those with a complete conclusion (what the session found + confidence). */
  concludedJobIds?: ReadonlySet<string>;
  /** OCT28 #14: the technician's choice, when offersEntireRepairChoice (null = not chosen yet). */
  stopScope?: StopScope | null;
}

export interface StopConfirmState {
  canConfirm: boolean;
  /** Why the confirm is disabled, in the technician's words (audit D-3: never silent). */
  blockers: string[];
}

/**
 * oct20 — when the Stop dialog may be confirmed, and why not.
 *
 * Every diagnostic/inspection job in my work needs an answer (findings, or
 * "No — not completed"). "No" is accepted for an INSPECTION on every Stop
 * (FD-03; CORE-I :124 — inspection is not diagnostic competency). For a
 * DIAGNOSTIC job "No" is accepted only when this Stop does not end the
 * diagnostic work (a colleague still works, or the repair continues on its
 * next day) — and then (oct20a S1) with what this session found (a session
 * conclusion, not the diagnosis result); when it does end the diagnosis, CORE-AJ :46 decides — "a diagnostic session must not
 * end without an outcome" — so the outcome is asked for, with the reason and
 * the way forward, instead of a silently disabled button.
 */
export function stopConfirmState(i: StopConfirmInput): StopConfirmState {
  const blockers: string[] = [];
  if (!i.ctxLoaded) return { canConfirm: false, blockers: ['Checking what this Stop will do…'] };
  if (offersEntireRepairChoice(i.ctx) && !i.stopScope) {
    blockers.push("Choose Today's Work Complete or Entire Repair Complete.");
  }
  const endsRepair = stopEndsRepair(i.ctx, i.stopScope ?? null);
  const pending = i.candidates.filter((c) => !i.capturedJobIds.has(c.jobId) && !i.skippedJobIds.has(c.jobId));
  if (pending.length > 0) {
    blockers.push(`Answer "Is this completed right now?" for ${pending.map((c) => c.jobName).join(', ')}.`);
  }
  const diagNo = endsRepair
    ? i.candidates.filter((c) => c.lockType === 'diagnostic' && i.skippedJobIds.has(c.jobId) && !i.capturedJobIds.has(c.jobId))
    : [];
  if (diagNo.length > 0) {
    blockers.push(`${diagNo.map((c) => c.jobName).join(', ')}: a diagnosis cannot end without its outcome. `
      + 'Answer Yes and record what you found so far (for example "Partial — narrowed it down"), or Pause to keep working on it later.');
  }
  // The server's own list (my_finish_work_context) — e.g. when the candidate read failed.
  if (endsRepair && !i.endingPausedWork) {
    const extra = (i.ctx?.diagnosticOutcomeRequired ?? [])
      .filter((d) => !i.capturedJobIds.has(d.jobId) && !i.candidates.some((c) => c.jobId === d.jobId));
    if (extra.length > 0) {
      blockers.push(`${extra.map((d) => d.name).join(', ')}: record the diagnostic outcome before you stop.`);
    }
  }
  if (endsRepair && i.endingPausedWork && (i.ctx?.diagnosticOutcomeRequired ?? []).length > 0) {
    blockers.push(`${(i.ctx?.diagnosticOutcomeRequired ?? []).map((d) => d.name).join(', ')}: a diagnosis cannot end `
      + 'without its outcome. Resume, then Stop to record what you found.');
  }
  // oct20a S1: a Stop that ends my diagnostic SESSION but not the diagnosis needs what my session found.
  const unconcluded = (i.sessionConclusionJobs ?? []).filter((j) => !(i.concludedJobIds ?? NO_JOB_IDS).has(j.jobId));
  if (unconcluded.length > 0) {
    blockers.push(`${unconcluded.map((j) => j.name).join(', ')}: say what your session found and how sure you are `
      + '(it is not the final diagnosis — whoever finishes it records that), or answer Yes above if the diagnosis is done.');
  }
  if (!allDispositioned(i.unresolvedJobs, i.dispositions)) {
    blockers.push('Say what happened to every job listed (completed, not performed, or not finished), or return to work.');
  }
  if (!i.locationOk) blockers.push('Choose where you are leaving the vehicle.');
  return { canConfirm: blockers.length === 0, blockers };
}

const NO_JOB_IDS: ReadonlySet<string> = new Set();

/**
 * oct20a S1 — the diagnostic jobs whose SESSION conclusion this Stop needs: only when it does not end the
 * diagnosis (a colleague still works it, or the repair continues on its next day). The server's list
 * (my_finish_work_context, or a SESSION_CONCLUSION_REQUIRED refusal), minus any job whose outcome this
 * Stop records (a running Stop's "Yes" answers — a paused End Work records none).
 */
export function sessionConclusionJobs(i: {
  ctx: FinishWorkContext | null;
  serverJobs: ReadonlyArray<{ jobId: string; name: string }>;
  capturedJobIds: ReadonlySet<string>;
  endingPausedWork: boolean;
}): Array<{ jobId: string; name: string }> {
  if (i.ctx && stopEndsRepair(i.ctx) && i.serverJobs.length === 0) return [];
  const all = [...(i.ctx?.diagnosticSessionConclusionRequired ?? [])];
  for (const j of i.serverJobs) if (!all.some((a) => a.jobId === j.jobId)) all.push(j);
  return all.filter((j) => i.endingPausedWork || !i.capturedJobIds.has(j.jobId));
}

/**
 * oct20 FD-02 — the job answers the Stop sends: the answers to every job the
 * Stop asked about (always — the server may have named them), plus every job
 * the technician marked "not finished" and every INSPECTION answered "No — not
 * completed" (left open, never completed by the Stop). Those NOT_FINISHED
 * answers are omitted only when the Stop is KNOWN not to end the repair (a
 * colleague still works, or the repair continues on its next day); with no
 * facts at all they are sent, and the server — which ignores them on a
 * non-final Stop — decides.
 */
export function stopJobDispositions(i: {
  /** false only when the facts say this Stop does not end the repair. */
  mayEndRepair: boolean;
  unresolvedJobs: ReadonlyArray<{ jobId: string }>;
  dispositions: Readonly<Record<string, JobDisposition>>;
  unfinishedJobIds: ReadonlySet<string>;
  candidates: ReadonlyArray<StopCaptureCandidate>;
  skippedJobIds: ReadonlySet<string>;
  capturedJobIds: ReadonlySet<string>;
}): Record<string, JobDisposition> {
  const out: Record<string, JobDisposition> = {};
  for (const j of i.unresolvedJobs) if (i.dispositions[j.jobId]) out[j.jobId] = i.dispositions[j.jobId];
  if (!i.mayEndRepair) return out;
  for (const id of i.unfinishedJobIds) out[id] = 'NOT_FINISHED';
  for (const c of i.candidates) {
    if (c.lockType === 'inspection' && i.skippedJobIds.has(c.jobId) && !i.capturedJobIds.has(c.jobId)) out[c.jobId] = 'NOT_FINISHED';
  }
  return out;
}

/**
 * oct20 FD-02 — will this Stop hand the ticket to FOH NOT completed (as far as the client can tell)? OCT28 #14-c: an
 * inspection left incomplete does not withhold completion.
 */
export function stopWithholdsCompletion(
  ctx: FinishWorkContext | null, answers: Readonly<Record<string, JobDisposition>>,
  scope: StopScope | null = null, inspectionJobIds: ReadonlySet<string> = new Set(),
): boolean {
  if (!stopEndsRepair(ctx, scope)) return false;
  return Object.entries(answers).some(([id, d]) => d === 'NOT_FINISHED' && !inspectionJobIds.has(id))
    || (ctx?.completionBlockers ?? []).length > 0;
}

export type StopDialogMode = 'ONLY_MINE' | 'FINAL_ASK_LOCATION' | 'FINAL_STAYS';

export function stopDialogMode(ctx: FinishWorkContext): StopDialogMode {
  if (!ctx.finalWorker) return 'ONLY_MINE';
  return ctx.needsDestination ? 'FINAL_ASK_LOCATION' : 'FINAL_STAYS';
}

/**
 * The destination kinds offered, in the order they are shown. Only places a
 * COMPLETED car may stay: the final Stop completes the ticket, and a completed
 * visit is on site (CORE-AL). Off site is not offered — sending the car away
 * is not finishing it; the technician pauses instead.
 */
export const FINISH_DESTINATION_KINDS = [
  { kind: 'PARKING_SPOT', label: 'Parking spot' },
  { kind: 'ON_SITE_GENERAL', label: 'In the lot (no specific spot)' },
  { kind: 'ON_SITE_UNKNOWN_EXACT', label: 'On site, spot unknown' },
] as const satisfies ReadonlyArray<{ kind: FinishDestination['kind']; label: string }>;

export const OFF_SITE_REASON_LABELS: Record<OffSiteReason, string> = {
  TEST_DRIVE: 'Test drive',
  SUBLET: 'Sublet',
  OTHER: 'Other',
};

/** A destination is only submittable once it says ONE complete thing. */
export function isCompleteDestination(d: Partial<FinishDestination> | null | undefined): d is FinishDestination {
  if (!d?.kind) return false;
  switch (d.kind) {
    case 'PARKING_SPOT': return typeof (d as { parkingSpotId?: string }).parkingSpotId === 'string'
      && (d as { parkingSpotId: string }).parkingSpotId.length > 0;
    case 'OFF_SITE': return ['TEST_DRIVE', 'SUBLET', 'OTHER'].includes((d as { reason?: string }).reason ?? '');
    case 'ON_SITE_GENERAL':
    case 'ON_SITE_UNKNOWN_EXACT': return true;
    default: return false;
  }
}

/** The destination being built in the dialog (may be incomplete while the technician chooses). */
export type DestinationDraft =
  | { kind: 'PARKING_SPOT'; parkingSpotId?: string }
  | { kind: 'ON_SITE_GENERAL' }
  | { kind: 'ON_SITE_UNKNOWN_EXACT' }
  | { kind: 'OFF_SITE'; reason?: OffSiteReason }
  | null;

/** A draft is submittable as a FinishDestination only when it says one complete thing. */
export function draftToDestination(d: DestinationDraft): FinishDestination | null {
  if (!d) return null;
  switch (d.kind) {
    case 'PARKING_SPOT': return d.parkingSpotId ? { kind: 'PARKING_SPOT', parkingSpotId: d.parkingSpotId } : null;
    case 'OFF_SITE': return d.reason ? { kind: 'OFF_SITE', reason: d.reason } : null;
    default: return d;
  }
}

/** The wire shape finish_my_work validates. */
export function destinationPayload(d: FinishDestination | null): Record<string, string> | null {
  if (!d) return null;
  switch (d.kind) {
    case 'PARKING_SPOT': return { kind: d.kind, parking_spot_id: d.parkingSpotId };
    case 'OFF_SITE': return { kind: d.kind, off_site_reason: d.reason };
    default: return { kind: d.kind };
  }
}

/**
 * Where the vehicle is now, as a phrase that follows "the vehicle stays" —
 * never more precise than the truth (item #13: the Guided Start wording).
 */
export function finishLocationWords(
  location: FinishWorkContext['location'],
  spotLabel: (id: string) => string | null = () => null,
): string | null {
  if (!location) return null;
  switch (location.kind) {
    case 'PARKING_SPOT': {
      const label = location.parkingSpotId ? spotLabel(location.parkingSpotId) : null;
      return label ? `in Spot ${label}` : 'in its parking spot';
    }
    case 'ON_SITE_GENERAL': return 'in the lot';
    case 'ON_SITE_UNKNOWN_EXACT': return 'on site (spot unknown)';
    case 'OFF_SITE': {
      const reason = location.offSiteReason && location.offSiteReason !== 'OTHER'
        ? OFF_SITE_REASON_LABELS[location.offSiteReason as OffSiteReason] : null;
      return reason ? `off site — ${reason}` : 'off site';
    }
    case 'BAY': return 'in its bay';
    default: return null;
  }
}

/** oct20 — what else decides the consequence line (all optional: absent = today's wording). */
export interface StopConsequenceOptions {
  /** D-2: a rollover day with a live next day — the Stop ends today's work, not the repair. */
  segmentContinues?: boolean;
  /** FD-02: the Stop will hand the ticket to FOH NOT completed. */
  withheld?: boolean;
  /** Why (the server's words), if known. */
  withheldReasons?: readonly string[];
  /** Core-100 direction 9: a Detail Specialist on a mixed visit. */
  detailWithheld?: boolean;
  /** OCT28 #14: the two-choice question is shown — the line follows the choice (null = not chosen yet). */
  stopScope?: StopScope | null;
}

/** What the technician is told the Stop will do — before they confirm. */
export function stopConsequenceLine(mode: StopDialogMode, whereNow: string | null, opts: StopConsequenceOptions = {}): string {
  const vehicle = mode === 'FINAL_ASK_LOCATION' ? 'the vehicle is parked' : `the vehicle stays ${whereNow ?? 'where it is'}`;
  if (mode === 'ONLY_MINE' && opts.detailWithheld) {
    return 'Your detail work ends. This visit has other work, so it is not completed: the vehicle stays where it is and the front office finishes the visit.';
  }
  if (mode !== 'ONLY_MINE' && opts.segmentContinues && opts.stopScope === null) {
    return 'This repair is scheduled over more than one day. Choose what you finished.';
  }
  if (mode !== 'ONLY_MINE' && opts.segmentContinues && opts.stopScope === 'ENTIRE_REPAIR') {
    return `Entire repair complete: the repair is completed now, its later scheduled days are freed up, and it goes to the front office: ${vehicle}. `
      + 'The customer is not marked as picked up. If anything required is still open, nothing changes.';
  }
  if (mode !== 'ONLY_MINE' && opts.segmentContinues) {
    return `This is one day of a multi-day repair. Stopping ends today's work only — the repair is NOT completed and continues on its next day: ${vehicle}.`;
  }
  if (mode !== 'ONLY_MINE' && opts.withheld) {
    const why = opts.withheldReasons && opts.withheldReasons.length > 0 ? ` (${opts.withheldReasons.join('; ')})` : '';
    return `You are the last technician on this ticket. Stopping ends your work but does NOT complete the ticket${why}: `
      + `unfinished work stays open, ${vehicle}, and the ticket goes to the front office to decide what happens next.`;
  }
  switch (mode) {
    case 'ONLY_MINE':
      return 'Another technician still has work on this ticket, so only your work stops. The vehicle stays where it is and the ticket stays with the shop.';
    case 'FINAL_ASK_LOCATION':
      return 'You are the last technician on this ticket. Stopping finishes the work and completes the ticket: the vehicle is parked, and the ticket goes to the front office.';
    case 'FINAL_STAYS':
      return `You are the last technician on this ticket. Stopping finishes the work and completes the ticket: the vehicle stays ${whereNow ?? 'where it is'}, and the ticket goes to the front office.`;
    default: {
      const never: never = mode;
      return String(never);
    }
  }
}
